export const CHECKOUT_V2_CANONICAL_FLAGS = Object.freeze([
  "storefront_merchandising_v2",
  "storefront_checkout_v2",
]);

export const isEnabledFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

export const isCheckoutV2Enabled = (settings = {}) => {
  return CHECKOUT_V2_CANONICAL_FLAGS.every((key) =>
    isEnabledFlag(settings[key]),
  );
};

const COMPATIBILITY_PAYMENT_FLAGS = Object.freeze([
  "enable_tamara",
  "enable_tamara_widgets",
  "enable_tabby",
  "enable_tabby_widgets",
  "enable_deema",
  "enable_benefit_transfer",
  "enable_knet_on_delivery",
]);

export const CHECKOUT_V2_COMPATIBILITY_CODES = Object.freeze({
  CURRENCY_CONVERSION: "currency_conversion_pending",
  DYNAMIC_DELIVERY_PRICE: "dynamic_delivery_price_pending",
  ADVANCED_PRODUCT: "advanced_product_pricing_pending",
  B2B: "business_order_pricing_pending",
  TABLE_SESSION: "table_order_pricing_pending",
  CAR_SESSION: "car_service_pricing_pending",
});

const getCartLines = (cart = {}) =>
  Array.isArray(cart)
    ? cart
    : [
        ...(Array.isArray(cart?.cart) ? cart.cart : []),
        ...(Array.isArray(cart?.promo_items) ? cart.promo_items : []),
      ];

export const hasUnsupportedCheckoutV2CartItems = (cart = {}) =>
  getCartLines(cart).some((line = {}) => {
    const productType = `${
      line.type_of_product ||
      line.product_type ||
      line.typeOfProduct ||
      line.productType ||
      line.product?.type_of_product ||
      line.product?.product_type ||
      line.product?.typeOfProduct ||
      line.product?.productType ||
      ""
    }`.toLowerCase();
    const bookingSlot = line.booking_slot ?? line.bookingSlot;
    const serviceResource = line.resource ?? line.resource_id;
    const serviceSlot = line.resourceSlot ?? line.resource_slot;
    const isPreordered = isEnabledFlag(
      line.is_preordered ??
        line.isPreordered ??
        line.product?.is_preordered ??
        line.product?.isPreordered,
    );
    const allowsPreordering = isEnabledFlag(
      line.allow_preordering ??
        line.allowPreordering ??
        line.product?.allow_preordering ??
        line.product?.allowPreordering,
    );
    const hasBookingSlot =
      bookingSlot !== null && bookingSlot !== undefined;
    const hasServiceResource =
      serviceResource !== null && serviceResource !== undefined;
    const hasServiceSlot =
      serviceSlot !== null && serviceSlot !== undefined;
    return (
      ["bookable", "service", "composite"].includes(productType) ||
      isPreordered ||
      allowsPreordering ||
      hasBookingSlot ||
      hasServiceResource ||
      hasServiceSlot
    );
  });

const isInternationalDelivery = ({ address = {}, context = {}, store = {} }) => {
  const fulfillmentType =
    context?.type ||
    (address?.selectedDeliveryType === 0 ||
    address?.selectedDeliveryType === "0"
      ? "delivery"
      : address?.selectedDeliveryType === 1 ||
          address?.selectedDeliveryType === "1"
        ? "pickup"
        : null);
  if (fulfillmentType !== "delivery") return false;
  const selectedCountry =
    context?.countryId ?? address?.country ?? address?.country_id;
  const baseCountry =
    store?.base_country_id ??
    store?.baseCountryId ??
    store?.baseCountryID;
  if (selectedCountry === null || selectedCountry === undefined) return false;
  if (baseCountry === null || baseCountry === undefined) return false;
  return `${selectedCountry}` !== `${baseCountry}`;
};

const getSpecialSession = (address = {}) => {
  if (address?.parkingNumber || address?.parking_number)
    return "car";
  if (
    address?.token ||
    address?.dineInSessionID ||
    address?.dine_in_session_id ||
    address?.dineInOrderID ||
    address?.dine_in_order_id
  )
    return "table";
  return null;
};

const resolveSelectedCurrencyIso = ({
  countries = [],
  selectedCurrencyCode,
  settings = {},
}) => {
  const selected = `${selectedCurrencyCode || ""}`.trim().toLowerCase();
  if (!selected) return `${settings.currency_iso || ""}`.toUpperCase();
  if (/^[a-z]{3}$/i.test(selected)) return selected.toUpperCase();
  const country = (countries || []).find(
    (item) => `${item.dial_code || item.dialCode || ""}`.toLowerCase() === selected,
  );
  return `${country?.currency_iso || country?.currencyIso || selected}`.toUpperCase();
};

/**
 * Describe the parts of checkout that still need a legacy authority source.
 *
 * These capabilities no longer decide whether the shopper sees checkout v2.
 * They let the v2 shell remain active while it truthfully shows an exact
 * quote, a pending/TBD price, or a special-order summary.
 */
export const getCheckoutV2Compatibility = (
  settings = {},
  {
    address = {},
    cart = {},
    context = {},
    isDistributorRoute = false,
    pathname = "",
    countries = [],
    selectedCurrencyCode = null,
    store = {},
  } = {},
) => {
  const issues = [];
  const activeCurrencyConverter =
    isEnabledFlag(settings.enable_currency_converter) &&
      isEnabledFlag(settings.enable_currency_converter_staff);
  const selectedCurrencyIso = resolveSelectedCurrencyIso({
    countries,
    selectedCurrencyCode,
    settings,
  });
  const baseCurrencyIso = `${settings.currency_iso || ""}`.toUpperCase();
  const convertedCurrencyActive = Boolean(
    activeCurrencyConverter &&
      selectedCurrencyIso &&
      baseCurrencyIso &&
      selectedCurrencyIso !== baseCurrencyIso,
  );
  const dynamicDeliveryProvider =
    isEnabledFlag(settings.enable_dhl_v2) ||
    isEnabledFlag(settings.enable_aramex) ||
    (isEnabledFlag(settings.enable_dhl) &&
      isEnabledFlag(settings.enable_dhl_dynamic_rate));
  const advancedCart = hasUnsupportedCheckoutV2CartItems(cart);
  const b2b =
    isEnabledFlag(settings.is_b2b) ||
    isEnabledFlag(settings.enable_b2b_storefront) ||
    isEnabledFlag(settings.distributor_experience) ||
    isDistributorRoute === true ||
    /(^|\/)distributor(\/|$)/i.test(pathname);
  const specialSession = getSpecialSession(address);
  const internationalDelivery = isInternationalDelivery({
    address,
    context,
    store,
  });

  // Conversion is presentation-only. Quotes, placement, and gateways remain
  // base-currency, so selecting another display currency never blocks v2.
  if (dynamicDeliveryProvider && internationalDelivery)
    issues.push(CHECKOUT_V2_COMPATIBILITY_CODES.DYNAMIC_DELIVERY_PRICE);
  if (advancedCart)
    issues.push(CHECKOUT_V2_COMPATIBILITY_CODES.ADVANCED_PRODUCT);
  if (b2b) issues.push(CHECKOUT_V2_COMPATIBILITY_CODES.B2B);
  if (specialSession === "table")
    issues.push(CHECKOUT_V2_COMPATIBILITY_CODES.TABLE_SESSION);
  if (specialSession === "car")
    issues.push(CHECKOUT_V2_COMPATIBILITY_CODES.CAR_SESSION);

  return {
    enabled: isCheckoutV2Enabled(settings),
    issues,
    // Compatibility codes describe which authority the server must consult;
    // they must not prevent the authoritative quote request itself. The quote
    // response owns the actual pending/TBD state.
    pricingPending: false,
    activeCurrencyConverter,
    convertedCurrencyActive,
    advancedCart,
    b2b,
    dynamicDeliveryProvider,
    internationalDelivery,
    selectedCurrencyIso,
    specialSession,
    paymentProviderFlags: COMPATIBILITY_PAYMENT_FLAGS.filter((key) =>
      isEnabledFlag(settings[key]),
    ),
  };
};

export const isCheckoutV2Eligible = (
  settings = {},
  options = {},
) => {
  // Eligibility is intentionally only the two-store-flag rollout gate. The
  // compatibility descriptor above controls in-flow TBD/pending states; it
  // must never route a flagged shopper back to the legacy checkout.
  void options;
  return isCheckoutV2Enabled(settings);
};
